import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { ColorDot, SeasonBadge } from './Badges';
import {
  ADD_BACK_LABEL, KEEP_LABEL, UNKEEP_LABEL, decidedByline, excludedMoveKey, planVetoKey, vetoByline,
} from './exclusions';
import { variantKeepPendingKey, variantPendingKey } from './counterList';
import { SHELF_LABEL, displayTitle, variantChipLabel, variantLabel } from './format';
import { C, MONO, TODAY_SKIP_LABEL } from './tokens';
import { RestockV2ExcludedMove, RestockV2ExcludedStyle, RestockV2ListExcludedVariant } from './types';

// 「Nothing vanishes silently」: every screen says what it is hiding and offers one tap back.
//   衣架  per rack: 已排除 N 款 · 查看
//   今天  bottom of the walk: 今天略過 N 件 · 查看
//   訂貨  under the store groups: 已排除 N 款 · 查看
//   選品  已排除: whole styles (不要這款) AND single colours/sizes (不要這個顏色／尺寸), as their
//         own rows — they are different decisions, so one list of them would be a lie.
// The rows come from the server's plan.excluded, never from local state, so Magnus and Adam see the
// same list and a veto set on the phone is still there on the office PC.

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderTop: '1px solid',
      borderColor: C.line, minWidth: 0,
    }}
  >
    {children}
  </Box>
);

// `text` is what the button SAYS (加回 for the 已排除 rows, 取消留著 for the 留著 ones); `label` is
// its accessible name, which always carries the SKU and colour so two rows never sound alike.
const AddBack: React.FC<{
  label: string; text?: string; disabled: boolean; onClick: () => void;
}> = ({ label, text, disabled, onClick }) => (
  <Box
    component="button" type="button" onClick={onClick} disabled={disabled} aria-label={label}
    sx={{
      ml: 'auto', flex: 'none', appearance: 'none', bgcolor: C.goWash, border: '1px solid',
      borderColor: C.go, color: C.go, borderRadius: '8px', px: 1.1, py: '5px', fontSize: 12.5,
      fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
      '&:disabled': { opacity: 0.5, cursor: 'default' },
    }}
  >
    {text || ADD_BACK_LABEL}
  </Box>
);

const Sku: React.FC<{ sku: string }> = ({ sku }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: MONO, fontSize: 11.5, fontWeight: 700, bgcolor: C.paper2, color: C.ink2,
      borderRadius: '3px', px: '4px', flex: 'none',
    }}
  >
    {sku}
  </Typography>
);

interface HiddenFoldProps {
  // 已排除 2 款 · 查看 — the whole affordance is this one dashed line.
  label: string;
  styles?: RestockV2ExcludedStyle[];
  moves?: RestockV2ExcludedMove[];
  variants?: RestockV2ListExcludedVariant[];
  // 留著 rows. Same fold machinery, opposite decision: these are pieces he PINNED to the rail, and
  // the button takes the pin off. They live in their own fold (see RackPanel) rather than mixed in
  // with 已排除 — 「I decided this stays」 and 「I decided this goes」 are not one list.
  kept?: RestockV2ListExcludedVariant[];
  pending: Record<string, boolean>;
  onAddBackStyle?: (sku: string) => void;
  onAddBackMove?: (row: RestockV2ExcludedMove) => void;
  onAddBackVariant?: (row: RestockV2ListExcludedVariant) => void;
  onUnkeep?: (row: RestockV2ListExcludedVariant) => void;
}

const HiddenFold: React.FC<HiddenFoldProps> = ({
  label, styles, moves, variants, kept, pending, onAddBackStyle, onAddBackMove, onAddBackVariant,
  onUnkeep,
}) => {
  const [open, setOpen] = useState(false);
  const styleRows = styles || [];
  const moveRows = moves || [];
  const variantRows = variants || [];
  const keptRows = kept || [];
  if (!styleRows.length && !moveRows.length && !variantRows.length && !keptRows.length) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        component="button" type="button" onClick={() => setOpen((old) => !old)} aria-expanded={open}
        sx={{
          width: '100%', appearance: 'none', cursor: 'pointer', border: '1px dashed',
          borderColor: C.line2, bgcolor: C.wash, borderRadius: '9px', px: 1, py: 0.9,
          fontSize: 12.5, fontWeight: 800, color: C.ink2, textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 0.75,
        }}
      >
        {label}
        <Box component="span" aria-hidden="true" sx={{ ml: 'auto', fontSize: 11, color: C.ink3 }}>
          {open ? '▾' : '▸'}
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ bgcolor: C.card, border: '1px solid', borderColor: C.line, borderTop: 0, borderRadius: '0 0 9px 9px' }}>
          {styleRows.map((row) => (
            <Row key={`style:${row.sku}`}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                  <Sku sku={row.sku} />
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '-.01em' }}>
                    {row.title ? displayTitle(row.title) : '（已不在目錄）'}
                  </Typography>
                  <SeasonBadge value={row.season} size="xs" />
                </Box>
                <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '2px' }}>
                  {vetoByline(row)}
                  {row.reason ? `：${row.reason}` : ''}
                </Typography>
                {row.note && <Typography sx={{ fontSize: 11.5, color: C.back, mt: '2px' }}>{row.note}</Typography>}
              </Box>
              <AddBack
                label={`${ADD_BACK_LABEL} ${row.sku}`} disabled={!!pending[planVetoKey(row.sku)]}
                onClick={() => onAddBackStyle && onAddBackStyle(row.sku)}
              />
            </Row>
          ))}
          {/* A 不要這個顏色／尺寸 row. Tagged 顏色／尺寸 and struck through, because the line above it
              may be a whole style and the two undo different things: 加回 here restores ONE colour
              and leaves the style exactly where it was. */}
          {variantRows.map((row) => {
            const chip = variantChipLabel(row);
            const name = `${ADD_BACK_LABEL} ${row.sku} ${chip}`;
            return (
              <Row key={`variant:${row.sku}:${row.variant_id}`}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <Sku sku={row.sku} />
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10, fontWeight: 800, borderRadius: '99px', px: '6px',
                        bgcolor: C.paper2, color: C.ink3, whiteSpace: 'nowrap', flex: 'none',
                      }}
                    >
                      顏色／尺寸
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13, fontWeight: 800, letterSpacing: '-.01em', color: C.ink2,
                        textDecoration: 'line-through',
                      }}
                    >
                      <ColorDot name={row.color} size={10} />
                      {chip}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '2px' }}>
                    {row.title ? displayTitle(row.title) : '（已不在目錄）'}
                    {' · '}
                    {decidedByline(row.decided_by, row.decided_at)}
                    {row.reason ? `：${row.reason}` : ''}
                  </Typography>
                  {/* The SKU or the colour has left the catalogue and the veto is still live. */}
                  {row.note && <Typography sx={{ fontSize: 11.5, color: C.back, mt: '2px' }}>{row.note}</Typography>}
                </Box>
                <AddBack
                  label={name} disabled={!!pending[variantPendingKey(row.sku, row.variant_id)]}
                  onClick={() => onAddBackVariant && onAddBackVariant(row)}
                />
              </Row>
            );
          })}
          {/* 留著 — 「it stays on the rail」. Not struck through (nothing was taken away) and
              tagged 留著 rather than 顏色／尺寸, because the undo here puts the piece BACK into the
              帶回 queue rather than back onto the list. 架上 N 件 rides along so he can see what he
              pinned without opening the rack. */}
          {keptRows.map((row) => {
            const chip = variantChipLabel(row);
            const shelf = Number.isFinite(Number(row.on_hand)) ? `${SHELF_LABEL} ${Number(row.on_hand)} 件` : '';
            return (
              <Row key={`kept:${row.sku}:${row.variant_id}`}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <Sku sku={row.sku} />
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10, fontWeight: 800, borderRadius: '99px', px: '6px',
                        bgcolor: C.goWash, color: C.go, whiteSpace: 'nowrap', flex: 'none',
                      }}
                    >
                      {KEEP_LABEL}
                    </Box>
                    <Typography
                      component="span"
                      sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '-.01em', color: C.ink }}
                    >
                      <ColorDot name={row.color} size={10} />
                      {chip}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '2px' }}>
                    {row.title ? displayTitle(row.title) : '（已不在目錄）'}
                    {shelf ? ` · ${shelf}` : ''}
                    {row.decided_by || row.decided_at ? ` · ${decidedByline(row.decided_by, row.decided_at)}` : ''}
                  </Typography>
                  {row.note && <Typography sx={{ fontSize: 11.5, color: C.back, mt: '2px' }}>{row.note}</Typography>}
                </Box>
                <AddBack
                  label={`${UNKEEP_LABEL} ${row.sku} ${chip}`} text={UNKEEP_LABEL}
                  disabled={!!pending[variantKeepPendingKey(row.sku, row.variant_id)]}
                  onClick={() => onUnkeep && onUnkeep(row)}
                />
              </Row>
            );
          })}
          {moveRows.map((row) => {
            const variant = variantLabel(row.color, row.size);
            const name = `${ADD_BACK_LABEL} ${row.sku}${variant ? ` ${variant}` : ''}`;
            return (
              <Row key={`move:${excludedMoveKey(row)}`}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <Sku sku={row.sku} />
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '-.01em' }}>
                      <ColorDot name={row.color} size={10} />
                      {variant || displayTitle(row.title)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '2px' }}>
                    {TODAY_SKIP_LABEL[row.move_kind] || row.move_kind}
                    {variant ? ` · ${displayTitle(row.title)}` : ''}
                  </Typography>
                </Box>
                <AddBack
                  label={name} disabled={!!pending[excludedMoveKey(row)]}
                  onClick={() => onAddBackMove && onAddBackMove(row)}
                />
              </Row>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default HiddenFold;
